import { email, required, useTranslate } from "ra-core";

import { BooleanInput } from "@/components/admin/boolean-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { Sale } from "@/components/atomic-crm/types";

import { claveOrigen, ORIGENES_ALTA } from "../dominio/catalogos";
import { useEsAdmin } from "../datos/useEsAdmin";
import { useFiltroAsignables } from "../datos/useFiltroAsignables";
import { useFiltroEtapas } from "../datos/useFiltroEtapas";
import {
  claveEtapa,
  esEtapaDelComercial,
  ETAPAS_ALTA,
} from "../dominio/etapas";
import { CamposPorEtapa } from "./CamposPorEtapa";
import { PosibleDuplicado } from "./PosibleDuplicado";
import { SelectorSector } from "./SelectorSector";

const nombreUsuario = (r: Sale) => `${r.first_name} ${r.last_name}`;

/**
 * Campos del alta de negocio (SPEC.md §1). Con `compacto`, el alta rápida de la
 * libreta de campo: lo esencial arriba y el resto plegado en «Más datos». La etapa se
 * puede elegir para crear la ficha directamente en el pipeline (contacto previo).
 * «No volver a contactar» no está en el alta (v1.1): se marca desde la ficha o con el
 * resultado «No le interesa». Un setter no elige dueño: la ficha es suya (S10), y si el
 * negocio ya está en el CRM la base lo rechaza con «Este negocio ya está en el CRM», sin
 * decir de quién es.
 */
export const NegocioInputs = ({ compacto = false }: { compacto?: boolean }) => {
  const translate = useTranslate();
  const origenes = ORIGENES_ALTA.map((o) => ({
    id: o,
    name: translate(claveOrigen(o)),
  }));
  // S10: un setter da de alta sus fichas (dueño él) y solo en sus etapas.
  const esAdmin = useEsAdmin();
  const filtroAsignables = useFiltroAsignables();
  const filtroEtapas = useFiltroEtapas();
  const etapas = filtroEtapas(ETAPAS_ALTA)
    .filter((e) => esAdmin || esEtapaDelComercial(e))
    .map((e) => ({
      id: e,
      name: translate(claveEtapa(e)),
    }));

  const esenciales = (
    <>
      <TextInput
        source="nombre"
        validate={required()}
        helperText={false}
        autoFocus
      />
      <PosibleDuplicado />
      <TextInput source="telefono" type="tel" helperText={false} />
      <TextInput source="direccion" helperText={false} />
      <SelectorSector />
      <TextInput source="persona_contacto" helperText={false} />
      <BooleanInput source="es_decisor" helperText={false} />
    </>
  );

  const campoEtapa = (
    <>
      <SelectInput
        source="etapa"
        choices={etapas}
        validate={required()}
        helperText={false}
      />
      <CamposPorEtapa />
    </>
  );

  const resto = (
    <>
      <TextInput source="cargo_contacto" helperText={false} />
      <TextInput
        source="email"
        type="email"
        validate={email()}
        helperText={false}
      />
      <TextInput source="web" helperText={false} />
      {campoEtapa}
      <SelectInput
        source="origen"
        choices={origenes}
        validate={required()}
        helperText={false}
      />
      <ReferenceInput
        source="asignado_a"
        reference="sales"
        filter={filtroAsignables}
      >
        <SelectInput
          optionText={nombreUsuario}
          helperText={false}
          emptyText={translate("nevora.negocios.sin_asignar")}
          readOnly={!esAdmin}
        />
      </ReferenceInput>
      {esAdmin ? (
        // 36: sin importes para el setter antes de «Aceptada».
        <NumberInput
          source="valor_estimado"
          min={0}
          step={100}
          helperText={false}
        />
      ) : null}
      <TextInput source="siguiente_paso" helperText={false} />
      <DateInput source="fecha_siguiente_paso" helperText={false} />
    </>
  );

  if (compacto) {
    return (
      <div className="flex flex-col gap-4">
        {esenciales}
        <Accordion type="single" collapsible>
          <AccordionItem value="mas">
            <AccordionTrigger className="text-sm">
              {translate("nevora.negocios.mas_datos")}
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 pt-2">
              {resto}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 w-full">
      <div className="flex flex-col gap-4">
        <h3 className="etiqueta">
          {translate("nevora.negocios.datos_contacto")}
        </h3>
        {esenciales}
        <TextInput source="cargo_contacto" helperText={false} />
        <TextInput
          source="email"
          type="email"
          validate={email()}
          helperText={false}
        />
        <TextInput source="web" helperText={false} />
      </div>
      <div className="flex flex-col gap-4">
        <h3 className="etiqueta">{translate("nevora.negocios.seguimiento")}</h3>
        {campoEtapa}
        <SelectInput
          source="origen"
          choices={origenes}
          validate={required()}
          helperText={false}
        />
        <ReferenceInput
          source="asignado_a"
          reference="sales"
          filter={filtroAsignables}
        >
          <SelectInput
            optionText={nombreUsuario}
            helperText={false}
            emptyText={translate("nevora.negocios.sin_asignar")}
            readOnly={!esAdmin}
          />
        </ReferenceInput>
        {esAdmin ? (
          // 36: sin importes para el setter antes de «Aceptada».
          <NumberInput
            source="valor_estimado"
            min={0}
            step={100}
            helperText={false}
          />
        ) : null}
        <TextInput source="siguiente_paso" helperText={false} />
        <DateInput source="fecha_siguiente_paso" helperText={false} />
      </div>
    </div>
  );
};
